import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Users, UserMinus, TrendingUp, FileSpreadsheet } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";
import { toast } from "sonner";
import BackupButton from "@/components/BackupButton";

const COMPANIES = [
  { key: "misas", label: "Misaş", cls: "company-misas", color: "#EF4444", sharesKey: "misas_shares" },
  { key: "mis_holding", label: "Mis Holding", cls: "company-mh", color: "#3B82F6", sharesKey: "mis_holding_shares" },
  { key: "koop", label: "Mis Koop.", cls: "company-koop", color: "#10B981", sharesKey: "koop_shares" },
];

async function downloadRoster(company, label) {
  try {
    const r = await axios.get(`${API}/members/roster/${company}`, { responseType: "blob", withCredentials: true });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = `toplanti-tutanagi-${company}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
    toast.success(`${label} tutanağı indirildi`);
  } catch (err) {
    toast.error("Tutanak indirilemedi");
  }
}

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    axios.get(`${API}/members/stats`).then(r => setStats(r.data));
  }, []);
  if (!stats) return <div className="text-slate-500 text-sm">Yükleniyor...</div>;

  const chartData = COMPANIES.map(c => ({ name: c.label, value: stats[c.key], color: c.color }));

  return (
    <div className="space-y-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Genel Bakış</div>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">Ortaklık Paneli</h1>
          <p className="text-slate-600 mt-2 text-sm">Üç şirketin aktif ortak sayıları ve toplam hisse dağılımı. Her şirket için toplantı imza tutanağını Excel olarak indirebilirsiniz.</p>
        </div>
        <BackupButton />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {COMPANIES.map((c, i) => (
          <div key={c.key} data-testid={`kpi-${c.key}`} className="crisp-card p-6 stagger-row" style={{ animationDelay: `${i*60}ms` }}>
            <div className="flex items-start justify-between">
              <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase tracking-wider ${c.cls}`}>
                {c.label}
              </span>
              <Users size={18} className="text-slate-400" />
            </div>
            <div className="mt-6 flex items-baseline gap-2">
              <div className="text-5xl font-extrabold text-slate-900 font-mono-nums">{stats[c.key]}</div>
              <div className="text-slate-500 text-sm">ortak</div>
            </div>
            <div className="mt-2 text-xs text-slate-500 font-mono-nums">
              Toplam hisse: <span className="text-slate-800 font-semibold">{stats[c.sharesKey].toLocaleString("tr-TR")}</span>
            </div>
            <button
              data-testid={`roster-${c.key}`}
              onClick={() => downloadRoster(c.key, c.label)}
              className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white text-slate-800 hover:bg-slate-900 hover:text-white transition-colors font-semibold text-sm py-2.5"
              title="Toplantı İmza Tutanağı Excel">
              <FileSpreadsheet size={15} /> Tutanak (Excel)
            </button>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="crisp-card p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-slate-900">Şirketlere Göre Ortak Dağılımı</h3>
            <TrendingUp size={18} className="text-slate-400" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={chartData} dataKey="value" nameKey="name" innerRadius={70} outerRadius={110} paddingAngle={2}>
                  {chartData.map((e,i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="crisp-card p-6">
          <h3 className="text-lg font-bold text-slate-900 mb-4">Özet</h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center justify-between">
              <span className="text-slate-600">Toplam aktif ortak</span>
              <span className="font-bold text-slate-900 font-mono-nums">{stats.total_active}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-slate-600 inline-flex items-center gap-2"><UserMinus size={14}/> Ayrılan ortaklar</span>
              <span className="font-bold text-slate-900 font-mono-nums">{stats.departed}</span>
            </li>
            <li className="flex items-center justify-between border-t border-slate-100 pt-3">
              <span className="text-slate-600">Misaş hisse</span>
              <span className="font-semibold font-mono-nums">{stats.misas_shares.toLocaleString("tr-TR")}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-slate-600">Mis Holding hisse</span>
              <span className="font-semibold font-mono-nums">{stats.mis_holding_shares.toLocaleString("tr-TR")}</span>
            </li>
            <li className="flex items-center justify-between">
              <span className="text-slate-600">Koop. hisse</span>
              <span className="font-semibold font-mono-nums">{stats.koop_shares.toLocaleString("tr-TR")}</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
}
